import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Phone, Calendar as CalendarIcon } from 'lucide-react';
import { format, addDays, subDays, isToday, getHours } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAppointments } from '../../../lib/hooks/useAppointments';
import { Appointment } from '../../../lib/interfaces/appointment.interface';
import { cn } from '../../../lib/utils/utils';
import { useUser } from '../../../lib/hooks/use-user';

interface DealCalendarSidebarProps {
  dealId: string;
}

export const DealCalendarSidebar: React.FC<DealCalendarSidebarProps> = ({ dealId }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const { appointments, loading, refetch } = useAppointments({ dealId });
  const { user } = useUser();
  const scrollRef = useRef<HTMLDivElement>(null);
  
  // Update current time every minute and scroll to current time
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);

  // Scroll to current time if viewing today
  useEffect(() => {
    if (isToday(selectedDate) && scrollRef.current) {
      const currentHour = getHours(currentTime);
      const scrollPosition = currentHour * 50; // 50px per hour slot
      scrollRef.current.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [selectedDate, currentTime]);

  // Listen for appointment changes and refresh data
  useEffect(() => {
    const handleAppointmentChange = () => {
      if (refetch) {
        refetch();
      }
    };

    // Listen for custom events that indicate appointment changes
    window.addEventListener('appointmentCreated', handleAppointmentChange);
    window.addEventListener('appointmentUpdated', handleAppointmentChange);
    window.addEventListener('appointmentDeleted', handleAppointmentChange);

    return () => {
      window.removeEventListener('appointmentCreated', handleAppointmentChange);
      window.removeEventListener('appointmentUpdated', handleAppointmentChange);
      window.removeEventListener('appointmentDeleted', handleAppointmentChange);
    };
  }, [refetch]);

  // Generate time slots from 00:00 to 23:00
  const timeSlots = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });

  // Get appointments for selected date
  const dayAppointments = appointments.filter(apt => {
    const aptDate = new Date(apt.start);
    return format(aptDate, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
  });

  // Function to check if an appointment is in this time slot
  const getAppointmentForTimeSlot = (timeSlot: string): Appointment | undefined => {
    const [hour] = timeSlot.split(':');
    return dayAppointments.find(apt => {
      const aptHour = new Date(apt.start).getHours();
      return aptHour === parseInt(hour);
    });
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    setSelectedDate(direction === 'next' ? addDays(selectedDate, 1) : subDays(selectedDate, 1));
  };

  return (
    <div className="flex flex-col bg-white rounded-lg shadow-sm border" style={{ maxHeight: '600px' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-gray-50">
        <button
          onClick={() => navigateDay('prev')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        <h3 className="font-medium text-sm flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-gray-500" />
          {format(selectedDate, 'EEEE, d. MMMM', { locale: de })}
        </h3>
        
        <button
          onClick={() => navigateDay('next')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Scrollable time slots */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" style={{ maxHeight: '480px' }}>
        <div className="relative">
          {timeSlots.map((timeSlot, index) => {
            const appointment = getAppointmentForTimeSlot(timeSlot);
            const [slotHour] = timeSlot.split(':').map(Number);
            const isToday = format(currentTime, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
            const currentHour = currentTime.getHours();
            const currentMinutes = currentTime.getMinutes();
            const isCurrentHour = isToday && currentHour === slotHour;
            
            // Calculate the position of the red line within the hour slot
            const minuteProgress = currentMinutes / 60;
            const showRedLine = isToday && isCurrentHour;
            
            return (
              <div key={timeSlot} className="relative">
                {/* Time slot row */}
                <div className="flex items-stretch border-b border-gray-100" style={{ height: '48px' }}>
                  {/* Time label */}
                  <div className="w-14 px-2 py-2 text-xs text-gray-500 flex-shrink-0 font-medium">
                    {timeSlot}
                  </div>
                  
                  {/* Content area */}
                  <div className="flex-1 relative">
                    {/* Current time indicator */}
                    {showRedLine && (
                      <div 
                        className="absolute left-0 right-0 h-0.5 bg-red-500 z-20"
                        style={{ top: `${minuteProgress * 100}%` }}
                      >
                        <div className="absolute -left-2 -top-1.5 w-3 h-3 bg-red-500 rounded-full" />
                        {/*<span className="absolute -left-12 -top-2.5 text-xs text-red-500 font-medium">
                          {format(currentTime, 'HH:mm')}
                        </span>*/}
                      </div>
                    )}
                    
                    {/* Appointment block */}
                    {appointment && (
                      <div
                        className={cn(
                          "absolute inset-x-0 mx-1 my-1 p-2 rounded-md text-xs",
                          "bg-blue-500 text-white cursor-pointer hover:bg-blue-600 transition-colors",
                          "flex items-center gap-1 shadow-sm"
                        )}
                        style={{
                          minHeight: '42px'
                        }}
                      >
                        {appointment.type === 'CALL' && <Phone className="h-3 w-3" />}
                        <div className="truncate">
                          <div className="font-medium truncate">{appointment.title}</div>
                          <div className="opacity-90 text-xs">
                            {format(new Date(appointment.start), 'HH:mm')} - 
                            {format(new Date(appointment.end), 'HH:mm')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom button */}
      <div className="p-3 border-t bg-gray-50">
        <button
          onClick={() => navigateDay('next')}
          className="w-full py-2 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors font-medium"
        >
          Nächster Tag
        </button>
      </div>
    </div>
  );
};